"use client";

import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import { Button } from "@workspace/ui/components/button";
import { Badge } from "@workspace/ui/components/badge";
import { Pencil, Check, X, FileText, Wand2, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/api-client";
import type { Frontmatter } from "@/lib/frontmatter/types";
import { DOC_TYPE_LABELS, DOC_TYPES } from "@/lib/frontmatter/types";
import { FieldSection, FieldRow } from "./properties/field-section";
import { StatusBadge } from "./properties/status-badge";
import { SingleUserField, MultiUserField } from "./properties/user-chip";
import { DocLinkChips, SingleDocLink } from "./properties/doc-link-chips";
import { TagBadges } from "./properties/tag-badges";
import { DateField } from "./properties/date-field";
import { EnumField } from "./properties/enum-field";
import { TextField } from "./properties/text-field";
import { TasksField } from "./properties/tasks-field";
import { TypeSpecificSection } from "./properties/type-specific-section";

const fetcher = <T,>(url: string): Promise<T> =>
  apiClient.get<T>(url).then((r) => r.data);

interface PropertiesPanelProps {
  documentId: string;
}

/** Get the editor's current markdown via the event/response pair set up in editor.tsx */
function getEditorMarkdown(): Promise<string> {
  return new Promise((resolve) => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      window.removeEventListener("docs:editor-markdown-response", handler);
      resolve(typeof detail === "string" ? detail : "");
    };
    window.addEventListener("docs:editor-markdown-response", handler);
    window.dispatchEvent(new CustomEvent("docs:get-editor-markdown"));
    setTimeout(() => {
      window.removeEventListener("docs:editor-markdown-response", handler);
      resolve("");
    }, 1500);
  });
}

/** Crude in-browser frontmatter detection — checks for leading `---\n...---` block */
function hasFrontmatterBlock(markdown: string): boolean {
  return /^---\s*\n[\s\S]*?\n---\s*\n/.test(markdown);
}

export function PropertiesPanel({ documentId }: PropertiesPanelProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [saving, setSaving] = useState(false);
  const [extractAvailable, setExtractAvailable] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);

  const { data: doc, mutate } = useSWR<{
    frontmatter?: string | null;
  }>(`/api/documents/${documentId}`, fetcher);

  const frontmatter: Frontmatter | null = doc?.frontmatter
    ? JSON.parse(doc.frontmatter)
    : null;

  const current = (editing && draft ? draft : frontmatter) as Frontmatter | null;

  const startEditing = useCallback(() => {
    setDraft(frontmatter ? { ...frontmatter } : {});
    setEditing(true);
  }, [frontmatter]);

  const cancelEditing = useCallback(() => {
    setDraft(null);
    setEditing(false);
  }, []);

  const updateField = useCallback(
    (key: string, value: unknown) => {
      setDraft((prev) => (prev ? { ...prev, [key]: value } : { [key]: value }));
    },
    []
  );

  const save = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await apiClient.patch(`/api/documents/${documentId}`, {
        frontmatter: draft,
      });
      await mutate();
      setEditing(false);
      setDraft(null);
    } finally {
      setSaving(false);
    }
  }, [draft, documentId, mutate]);

  // When the document has no frontmatter in the DB, peek at the editor body
  // to see if a YAML block is sitting at the top (legacy imports).
  useEffect(() => {
    if (doc?.frontmatter) {
      setExtractAvailable(false);
      return;
    }
    let cancelled = false;
    // Wait briefly for the editor to mount/sync before probing
    const timer = setTimeout(async () => {
      const md = await getEditorMarkdown();
      if (!cancelled) setExtractAvailable(hasFrontmatterBlock(md));
    }, 800);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [doc?.frontmatter]);

  const extractFrontmatter = useCallback(async () => {
    setExtracting(true);
    setExtractError(null);
    try {
      const md = await getEditorMarkdown();
      if (!hasFrontmatterBlock(md)) {
        setExtractError("No YAML block found at the top of the document.");
        return;
      }

      // The server tries multiple strategies (direct parse, disk recovery
      // for engineering docs, heuristic key-split) before giving up.
      const result = await apiClient.safeRequest<{
        strippedMarkdown?: string;
        hint?: string;
        error?: string;
      }>(`/api/documents/${documentId}/extract-frontmatter`, {
        method: "POST",
        body: { markdown: md },
      });
      if (result.ok) {
        const { strippedMarkdown } = result.data;
        if (strippedMarkdown) {
          window.dispatchEvent(
            new CustomEvent("docs:set-editor-markdown", {
              detail: { markdown: strippedMarkdown },
            })
          );
        }
        await mutate();
        setExtractAvailable(false);
      } else {
        const data = (result.error.responseData ?? {}) as {
          hint?: string;
          error?: string;
        };
        setExtractError(
          data.hint ?? data.error ?? "Could not extract frontmatter."
        );
      }
    } catch (err) {
      setExtractError(
        err instanceof Error ? err.message : "Extraction failed."
      );
    } finally {
      setExtracting(false);
    }
  }, [documentId, mutate]);

  if (!current) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-0 text-center p-4 overflow-y-auto">
        <FileText className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground mb-3">
          No metadata yet
        </p>
        {extractAvailable ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              YAML frontmatter detected at the top of this document.
              Extract it into structured metadata?
            </p>
            <Button
              variant="default"
              size="sm"
              onClick={extractFrontmatter}
              disabled={extracting}
            >
              {extracting ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              Extract from document
            </Button>
            {extractError && (
              <p className="text-sm text-destructive mt-3 max-w-[280px]">
                {extractError}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              Import an engineering doc or add metadata manually
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraft({ type: "tdd", status: "draft" });
                setEditing(true);
              }}
            >
              Add metadata
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          {current.type && (
            <Badge variant="outline" className="text-sm px-2 py-0.5 font-medium">
              {DOC_TYPE_LABELS[current.type] ?? current.type}
            </Badge>
          )}
          {current.id && (
            <span className="text-sm text-muted-foreground font-mono">
              {current.id}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={cancelEditing}
                disabled={saving}
              >
                <X className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-primary"
                onClick={save}
                disabled={saving}
              >
                <Check className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={startEditing}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {/* Identity */}
        <FieldSection title="Identity" defaultOpen>
          {editing && (
            <FieldRow label="Type">
              <EnumField
                value={current.type}
                options={[...DOC_TYPES]}
                editing={editing}
                onChange={(v) => updateField("type", v)}
              />
            </FieldRow>
          )}
          <FieldRow label="Status">
            <StatusBadge
              status={current.status || "draft"}
              docType={current.type}
              editing={editing}
              onChange={(v) => updateField("status", v)}
            />
          </FieldRow>
          {(current.visibility || editing) && (
            <FieldRow label="Visibility">
              <EnumField
                value={current.visibility}
                options={["private", "team", "org", "public"]}
                editing={editing}
                onChange={(v) => updateField("visibility", v)}
              />
            </FieldRow>
          )}
          {(current.domain || editing) && (
            <FieldRow label="Domain">
              <TextField
                value={current.domain}
                editing={editing}
                onChange={(v) => updateField("domain", v)}
              />
            </FieldRow>
          )}
        </FieldSection>

        {/* Ownership */}
        <FieldSection title="Ownership" defaultOpen>
          <FieldRow label="Author">
            <SingleUserField
              value={current.author}
              editing={editing}
              onChange={(v) => updateField("author", v)}
            />
          </FieldRow>
          <FieldRow label="Owner">
            <SingleUserField
              value={current.owner}
              editing={editing}
              onChange={(v) => updateField("owner", v)}
            />
          </FieldRow>
          <FieldRow label="Reviewers">
            <MultiUserField
              value={current.reviewers}
              editing={editing}
              onChange={(v) => updateField("reviewers", v)}
            />
          </FieldRow>
          <FieldRow label="Approvers">
            <MultiUserField
              value={current.approvers}
              editing={editing}
              onChange={(v) => updateField("approvers", v)}
            />
          </FieldRow>
        </FieldSection>

        {/* Tasks */}
        <FieldSection title="Tasks" defaultOpen={(current.tasks?.length ?? 0) > 0}>
          <TasksField
            value={current.tasks}
            editing={editing}
            onChange={(v) => updateField("tasks", v)}
          />
        </FieldSection>

        {/* Timing */}
        <FieldSection title="Timing" defaultOpen={false}>
          <FieldRow label="Created">
            <DateField
              value={current.created}
              editing={editing}
              onChange={(v) => updateField("created", v)}
            />
          </FieldRow>
          <FieldRow label="Updated">
            <DateField
              value={current.updated}
              editing={editing}
              onChange={(v) => updateField("updated", v)}
            />
          </FieldRow>
          <FieldRow label="Reviewed">
            <DateField
              value={current.reviewed}
              editing={editing}
              onChange={(v) => updateField("reviewed", v)}
            />
          </FieldRow>
          {(current.review_frequency || editing) && (
            <FieldRow label="Frequency">
              <EnumField
                value={current.review_frequency}
                options={["none", "monthly", "quarterly", "annually", "on-change"]}
                editing={editing}
                onChange={(v) => updateField("review_frequency", v)}
              />
            </FieldRow>
          )}
          {(current.next_review || editing) && (
            <FieldRow label="Next review">
              <DateField
                value={current.next_review}
                editing={editing}
                onChange={(v) => updateField("next_review", v)}
              />
            </FieldRow>
          )}
        </FieldSection>

        {/* Scope */}
        <FieldSection title="Scope" defaultOpen={false}>
          <FieldRow label="Tags">
            <TagBadges
              value={current.tags}
              editing={editing}
              onChange={(v) => updateField("tags", v)}
            />
          </FieldRow>
          <FieldRow label="Services">
            <TagBadges
              value={current.services}
              editing={editing}
              onChange={(v) => updateField("services", v)}
              variant="outline"
            />
          </FieldRow>
          {(current.products?.length || editing) && (
            <FieldRow label="Products">
              <TagBadges
                value={current.products}
                editing={editing}
                onChange={(v) => updateField("products", v)}
                variant="outline"
              />
            </FieldRow>
          )}
        </FieldSection>

        {/* Relationships */}
        <FieldSection title="Relationships" defaultOpen={false}>
          <FieldRow label="Related">
            <DocLinkChips

              value={current.related}
              editing={editing}
              onChange={(v) => updateField("related", v)}
            />
          </FieldRow>
          <FieldRow label="Implements">
            <DocLinkChips

              value={current.implements}
              editing={editing}
              onChange={(v) => updateField("implements", v)}
            />
          </FieldRow>
          <FieldRow label="Supersedes">
            <DocLinkChips

              value={current.supersedes}
              editing={editing}
              onChange={(v) => updateField("supersedes", v)}
            />
          </FieldRow>
          <FieldRow label="Superseded by">
            <SingleDocLink
              value={current.superseded_by}
              editing={editing}
              onChange={(v) => updateField("superseded_by", v)}
            />
          </FieldRow>
          <FieldRow label="Motivated by">
            <DocLinkChips

              value={current.motivated_by}
              editing={editing}
              onChange={(v) => updateField("motivated_by", v)}
            />
          </FieldRow>
          <FieldRow label="Part of">
            <DocLinkChips

              value={current.part_of}
              editing={editing}
              onChange={(v) => updateField("part_of", v)}
            />
          </FieldRow>
          <FieldRow label="Depends on">
            <DocLinkChips

              value={current.depends_on}
              editing={editing}
              onChange={(v) => updateField("depends_on", v)}
            />
          </FieldRow>
        </FieldSection>

        {/* Type-specific fields */}
        {current.type && (
          <FieldSection title={DOC_TYPE_LABELS[current.type] ?? current.type} defaultOpen>
            <TypeSpecificSection
              frontmatter={current as Frontmatter}
              editing={editing}
              onUpdate={updateField}
            />
          </FieldSection>
        )}

        {/* Sync metadata (read-only) */}
        {(current.sync_source || current.platform_id) && (
          <FieldSection title="Sync" defaultOpen={false}>
            {current.sync_source && (
              <FieldRow label="Source">
                <Badge variant="outline" className="text-sm px-2 py-0.5 font-normal">
                  {current.sync_source}
                </Badge>
              </FieldRow>
            )}
            {current.platform_id && (
              <FieldRow label="Platform ID">
                <span className="text-sm text-muted-foreground font-mono break-all">
                  {current.platform_id}
                </span>
              </FieldRow>
            )}
            {current.generated_by && (
              <FieldRow label="Generated by">
                <span className="text-sm text-muted-foreground break-all">
                  {current.generated_by}
                </span>
              </FieldRow>
            )}
          </FieldSection>
        )}
      </div>
    </div>
  );
}
